import { type QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { type ApplicationBundle, listClientApplications } from "@/services/clientOnboarding";
import { OrgApiError, type PaginatedResponse } from "@/services/orgApiFetch";

import { useClientContextKey } from "./useClientContextKey";

/**
 * A 401 from the applications endpoint means the session is dead — the
 * access token survived a refresh round-trip inside `orgAuthFetch` and the
 * API still refused it (e.g. the user row was deleted server-side while the
 * JWT stayed signature-valid). No retry or later refetch can succeed, so
 * callers must stop fetching and run session-expired handling instead of
 * hammering the API on the normal retry/refetch cadence.
 */
export function isSessionExpiredError(error: unknown): boolean {
    return error instanceof OrgApiError && error.status === 401;
}

/** Retry policy for the applications query: mirror the app-wide default of
 *  two retries, except a session-expired 401 which is terminal on the first
 *  response. Exported for unit tests. */
export function shouldRetryClientApplications(failureCount: number, error: unknown): boolean {
    if (isSessionExpiredError(error)) {
        return false;
    }
    return failureCount < 2;
}

/**
 * How often to re-read while the client is waiting on somebody else.
 *
 * In review and while the agreements are out, the state that moves is not
 * the client's: the issuer approves, and DocuSign reports each signature.
 * A minute is well inside how fast either of those moves. In draft the
 * client owns every change, so polling would only burn requests.
 */
const WAITING_REFETCH_INTERVAL_MS = 60_000;

const WAITING_STATUSES: ReadonlySet<ApplicationBundle["application"]["status"]> = new Set([
    "in_review",
    "awaiting_agreements"
]);

/** Exported for unit tests: the polling cadence for a given application status. */
export function clientApplicationsRefetchInterval(
    status: ApplicationBundle["application"]["status"] | undefined
): number | false {
    return status && WAITING_STATUSES.has(status) ? WAITING_REFETCH_INTERVAL_MS : false;
}

/**
 * The caller's onboarding applications, shared (same queryKey) between
 * `OnboardingGate`, `OnboardingEntry` and the onboarding hub, so the gate's
 * fetch answers the entry's lite-vs-full branch from cache and the hub can
 * never paint a different application state than the gate is deciding on.
 * Centralised here so every observer carries the same 401-is-terminal retry
 * policy — the gate reacts to a session-expired error by clearing auth and
 * routing to /login.
 *
 * Window-focus refetch is on (against the app-wide default): the client
 * leaves this tab to sign in DocuSign and comes back expecting the page to
 * know.
 */
export function useClientOnboardingApplications({ enabled = true }: { enabled?: boolean } = {}): UseQueryResult<
    PaginatedResponse<ApplicationBundle>,
    Error
> {
    const contextKey = useClientContextKey();
    return useQuery<PaginatedResponse<ApplicationBundle>, Error>({
        queryKey: ["client-onboarding-applications", contextKey],
        queryFn: listClientApplications,
        enabled,
        staleTime: 30_000,
        retry: shouldRetryClientApplications,
        refetchOnWindowFocus: true,
        refetchInterval: (query) => clientApplicationsRefetchInterval(query.state.data?.data?.[0]?.application.status)
    });
}

/**
 * Patch one application's bundle in every cached applications entry.
 *
 * The query above is keyed `["client-onboarding-applications", contextKey]`,
 * and `setQueryData` is an exact-key match — a bare-key write would create a
 * second, unobserved cache entry and leave the gate reading stale data. This
 * helper prefix-matches (like `invalidateQueries`) via `setQueriesData`, so
 * the entry the gate actually observes is patched whatever its contextKey is.
 * Lives here so the key literal and its patching stay in one file.
 *
 * The caller derives the next bundle from the cached one rather than
 * handing over a whole bundle: the query polls while the client waits on
 * the issuer, so a refetch can land between a save starting and its
 * response arriving, and a wholesale write from render-time state would
 * put the older `agreements` and steps back.
 */
export function patchClientOnboardingApplicationsBundle(
    queryClient: QueryClient,
    applicationUuid: string,
    update: (prev: ApplicationBundle) => ApplicationBundle
): void {
    queryClient.setQueriesData<PaginatedResponse<ApplicationBundle>>(
        { queryKey: ["client-onboarding-applications"] },
        (prev) =>
            prev
                ? {
                      ...prev,
                      data: prev.data.map((b) => (b.application.uuid === applicationUuid ? update(b) : b))
                  }
                : prev
    );
}
